import {
  applyStateDelta,
  type GameModule,
  type GameTurnInput,
  type GameTurnOutcome,
  type SpriteStageWidget,
  type ViewSpec,
  type ViewWidget,
} from "@roqeet/core";

/**
 * 비주얼노벨 — 네이티브 게임 모듈(결정 #24/#25). GameModule 추상화가 "수치 진행형"
 * (텍스트 어드벤처·스탯 RPG)뿐 아니라 "관계·장면 진행형" 장르에도 맞음을 실증하는
 * 세 번째 네이티브 모듈. 호감도(stat-bar#27)·장면(sprite-stage#27)·선택지를 다룬다.
 *
 * 상태 모양(moduleState):
 *   { sceneBg, sceneMood, day, route, affection_<이름>: number ...,
 *     bgAssetId?, choices, ended, ending }
 *
 * 호감도 키 규약(결정 #26): 모델이 `@증가 affection_지호 5`처럼 낸다.
 * 턴 파서의 속성 값은 한글을 허용하므로(값=비공백 토큰) 한글 이름이 그대로 키가 된다.
 */

const DEFAULT_STATE = (): Record<string, unknown> => ({
  sceneBg: "",
  sceneMood: "",
  day: 1,
  route: "프롤로그",
});

const AFFECTION_RE = /^affection(?:_(.+))?$/;

/** 모델에 RoQeet 게임 명령 문법을 지시(결정 #26). 현재 장면·호감도를 함께 알려 일관성을 높인다. */
function protocolInstructions(state: Record<string, unknown>, t?: (k: string, ...args: any[]) => string): string {
  const day = num(state["day"], 1);
  const route = String(state["route"] ?? "프롤로그");
  const affections = collectAffections(state);
  
  const tf = (k: string, fallback: string, ...args: any[]) => t ? t(k, ...args) : fallback;

  const affLine =
    affections.length > 0
      ? affections.map((a) => `${a.name} ${a.value}`).join(", ")
      : tf("game.prompt.ta.p10", "아직 없음");
  const ended = state["ended"] === true;

  return [
    tf("game.prompt.vn.p1", "당신은 비주얼노벨의 내레이터이자 등장인물들입니다. 인물의 대사와 지문으로 장면을 이끌고, 플레이어의 선택이 관계와 이야기에 영향을 주게 하세요."),
    "본문(대사·지문) 뒤에 RoQeet 게임 명령으로 상태를 갱신하세요(필요할 때만, 정확한 문법으로).",
    "- 선택지: @선택지 { ... } 블록에 보통 2~3개를 한 줄씩 작성",
    "- 장면 전환: @장면 bg=카페 mood=설렘 (배경과 분위기)",
    "- 호감도 변화: @증가 affection_지호 5 또는 @감소 affection_지호 3 (인물 이름을 affection_ 뒤에 붙임)",
    "- 날짜/장 진행: @증가 day 1 / @설정 route = 2장",
    "- 엔딩: @설정 ended = true 와 함께 결말(해피/노멀/배드)을 서술",
    "감정선을 섬세하게, RoQeet 명령은 정확하게 쓰세요. 호감도가 충분히 쌓이거나 갈리면 엔딩으로 이끄세요.",
    "",
    tf("game.prompt.vn.p4", "현재 상태 — 호감도: {affection}/100, 현재 장면: {bg} (분위기: {mood}){ended}", 
      { affection: affLine, bg: String(state["sceneBg"] ?? ""), mood: String(state["sceneMood"] ?? ""), ended: ended ? tf("game.prompt.ta.p9", "(이야기 종료됨)") : "" })
  ].join("\n");
}

/** 파싱된 턴을 상태에 반영(결정 #26/#28). 장면 전환 + 호감도 갱신을 처리. */
function applyTurn(input: GameTurnInput): GameTurnOutcome {
  // 1) 상태 연산 반영(호감도 inc/dec, day, route, ended 등).
  let state = applyStateDelta(input.state, input.parsed.stateDelta);

  // 2) 장면 전환(parsed.scene)을 평탄한 키로 저장(결정 #28 직렬화 가능).
  if (input.parsed.scene) {
    const sc = input.parsed.scene;
    state = {
      ...state,
      ...(sc["bg"] !== undefined ? { sceneBg: sc["bg"] } : {}),
      ...(sc["mood"] !== undefined
        ? { sceneMood: sc["mood"] }
        : sc["sprite"] !== undefined
          ? { sceneMood: sc["sprite"] }
          : {}),
    };
  }

  // 3) 호감도는 0~100으로 클램프(결정 #28 상태 정합).
  for (const key of Object.keys(state)) {
    if (AFFECTION_RE.test(key)) {
      state[key] = clamp(num(state[key], 0), 0, 100);
    }
  }

  // 4) 선택지를 상태에 영속(결정 #28): viewSpec이 순수 함수가 되도록.
  state = { ...state, choices: input.parsed.choices };

  const ended = state["ended"] === true;
  const outcome: GameTurnOutcome = { state, ended };
  if (ended) outcome.endingLabel = String(state["ending"] ?? "엔딩");
  return outcome;
}

/** 현재 상태에서 렌더할 위젯 선언(결정 #27). */
function viewSpec(state: Record<string, unknown>, t?: (k: string, ...args: any[]) => string): ViewSpec {
  const affections = collectAffections(state);
  const sceneBg = String(state["sceneBg"] ?? "");
  const sceneMood = String(state["sceneMood"] ?? "");
  const day = num(state["day"], 1);
  const route = String(state["route"] ?? "");
  const choices = Array.isArray(state["choices"])
    ? (state["choices"] as unknown[]).map((x) => String(x))
    : [];
  const ended = state["ended"] === true;

  // HUD: 장(章)/날짜 + 호감도 바들.
  const hud: ViewWidget[] = [
    {
      kind: "stat-panel",
      stats: [
        ...(route ? [{ label: "Route", value: route }] : []),
        { label: "Day", value: day },
      ],
    },
    ...affections.map(
      (a): ViewWidget => ({
        kind: "stat-bar",
        label: `♥ ${a.name}`,
        value: a.value,
        max: 100,
        tone: "affection",
      }),
    ),
  ];

  const widgets: ViewWidget[] = [];

  // 스프라이트 무대(결정 #27): 배경 에셋이 바인딩된 경우에만(없으면 빈 박스 방지).
  const bgAssetId = state["bgAssetId"];
  if (typeof bgAssetId === "string" && bgAssetId) {
    const stage: SpriteStageWidget = { kind: "sprite-stage", backgroundAssetId: bgAssetId, sprites: [] };
    widgets.push(stage);
  } else if (sceneBg) {
    // 에셋이 없으면 장면을 텍스트로 안내(상태→뷰 실증).
    widgets.push({
      kind: "stat-panel",
      title: t ? t("gamePlay.snippet.scene", "장면") : "장면",
      stats: [
        { label: t ? t("gameWidget.backgroundAlt", "배경") : "배경", value: sceneBg },
        ...(sceneMood ? [{ label: "Mood", value: sceneMood }] : []),
      ],
    });
  }

  if (ended) {
    widgets.push({
      kind: "notice",
      tone: "success",
      text: `${t ? t("game.noticeEnd", "이야기가 끝났습니다") : "이야기가 끝났습니다"} — ${String(state["ending"] ?? (t ? t("game.endingEnd", "엔딩") : "엔딩"))}`,
    });
  } else if (choices.length > 0) {
    widgets.push({ kind: "choices", choices });
  }

  return { widgets, hud };
}

export const visualNovelModule: GameModule = {
  manifest: {
    id: "visual-novel",
    title: "비주얼노벨",
    titleKey: "game.mod.visualNovel.title",
    description: "관계와 선택으로 이야기가 갈리는 AI 비주얼노벨. 호감도·장면·멀티 엔딩.",
    descriptionKey: "game.mod.visualNovel.desc",
    kind: "native",
    icon: "mask",
  },
  initialState: DEFAULT_STATE,
  protocolInstructions,
  applyTurn,
  viewSpec,
};

/** affection_ 접두 키(+ 단독 affection)를 모아 표시용 목록으로 변환. */
function collectAffections(state: Record<string, unknown>): { name: string; value: number }[] {
  const out: { name: string; value: number }[] = [];
  for (const key of Object.keys(state)) {
    const m = AFFECTION_RE.exec(key);
    if (!m) continue;
    out.push({ name: m[1] ?? "호감도", value: clamp(num(state[key], 0), 0, 100) });
  }
  // 이름 가나다순으로 안정 정렬(렌더 흔들림 방지).
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

function num(v: unknown, fallback: number): number {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}
